import {NftHistory, StakingData} from '@/providers/overview/useStakingData';
import {
  ChartData,
  Position,
  PositionBalance,
  PositionEventType,
} from '@/providers/overview/OverviewProvider';
import {useMemo} from 'react';
import {formatEther} from 'ethers';
import {addPositionChartData} from '@/providers/overview/addPositionChartData';

export const PRECISION = 2n ** 64n - 1n;

export const useStakingPositions = (
  chartData: ChartData,
  stakingData?: StakingData
) => {
  return useMemo(() => {
    return parseStakingData(chartData, stakingData);
  }, [stakingData, chartData]);
};

function parseStakingData(chartData: ChartData, stakingData?: StakingData) {
  const chartDataItem = chartData['kei-finance'];

  if (!chartDataItem) {
    return [];
  }

  const stakingNfts: Record<string, Position<NftHistory, NftHistory>> = {};

  stakingData?.nftHistories
    .concat()
    .reverse()
    .forEach(history => {
      if (
        history.endTimestamp &&
        history.endTimestamp < Date.now() / 1000 - 60 * 24 * 60 * 60
      ) {
        return;
      }

      if (!stakingNfts[history.id]) {
        stakingNfts[history.nft.id] = {
          id: history.nft.id,
          type: 'Stake',
          symbol: 'KEI',
          name: 'KEI Stake #' + history.nft.id,
          decimals: 18,
          image: history.image,
          color: chartDataItem.color,
          balances: [],
          events: [],
          growth: 0,
          chartData: JSON.parse(JSON.stringify(chartDataItem.timeSeries)),
          assets: [],
          balance: '0',
          value: 0,
          meta: history,
        };
      }

      const tokenPrice = chartDataItem.getPrice(history.startTimestamp, 0);

      function getValueAt(this: PositionBalance, timestamp: number) {
        return (
          +formatEther(this.balance) * chartDataItem.getPrice(timestamp, 0)
        );
      }

      stakingNfts[history.nft.id].balances.push({
        value: +formatEther(history.startTokenAmount) * tokenPrice,
        balance: history.startTokenAmount,
        timestamp: history.startTimestamp,
        meta: history,
        blockNumber: '0', // TODO update this
        getValueAt,
      });

      if (history.endTimestamp) {
        stakingNfts[history.nft.id].balances.push({
          value: 0,
          balance: '0',
          timestamp: history.endTimestamp,
          meta: history,
          blockNumber: '0', // TODO update this
          getValueAt,
        });
      } else {
        stakingNfts[history.nft.id].balances.push({
          value:
            +formatEther(history.startTokenAmount) *
            chartDataItem.getPrice(undefined, 0),
          balance: history.startTokenAmount, // TODO fetch the current balance of the token
          timestamp: Math.floor(Date.now() / 1000),
          meta: history,
          blockNumber: '0', // TODO update this
          getValueAt,
        });
      }
    });

  stakingData?.stakingRewards
    .concat()
    .reverse()
    .forEach(reward => {
      for (const position of Object.values(stakingNfts)) {
        for (let i = 0; i < position.balances.length; i++) {
          const nextBalance = position.balances[i + 1];
          const balance = position.balances[i];

          if (reward.staking.id !== balance.meta.balance.stake.staking.id) {
            break;
          }

          if (
            reward.createdAt >= balance.timestamp &&
            balance.balance !== '0' &&
            (!nextBalance || reward.createdAt < nextBalance.timestamp)
          ) {
            // then it accumulated some rewards

            const stakeAmount = balance.meta.balance.stakeAmount;
            const tokenAmount =
              (BigInt(reward.cumulativeValue) * BigInt(stakeAmount)) /
              PRECISION;

            position.events.push({
              type: PositionEventType.ACCUMULATE,
              amount: tokenAmount.toString(),
              value:
                +formatEther(tokenAmount.toString()) *
                chartDataItem.getPrice(reward.createdAt, 0),
              timestamp: reward.createdAt,
              symbol: 'KEI',
              decimals: 18,
              tx: '', // TODO get the tx hash
            });

            // Update all subsequent balances to include this reward
            for (let j = i + 1; j < position.balances.length; j++) {
              const subsequentBalance = position.balances[j];
              if (subsequentBalance.balance === '0') {
                break;
              }
              const updatedTokenAmount =
                BigInt(subsequentBalance.balance) +
                BigInt(tokenAmount.toString());
              position.balances[j] = {
                ...subsequentBalance,
                balance: updatedTokenAmount.toString(),
                value:
                  +formatEther(updatedTokenAmount.toString()) *
                  chartDataItem.getPrice(subsequentBalance.timestamp, 0),
              };
            }

            // Insert a new balance at the current position with updated token amount
            const newTokenAmount =
              BigInt(balance.balance) + BigInt(tokenAmount.toString());
            position.balances.splice(i + 1, 0, {
              value:
                +formatEther(newTokenAmount.toString()) *
                chartDataItem.getPrice(reward.createdAt, 0),
              balance: newTokenAmount.toString(),
              timestamp: reward.createdAt,
              meta: balance.meta,
              blockNumber: '0', // TODO update this
              getValueAt: balance.getValueAt,
            });

            // Skip the newly inserted balance in the next iteration
            i++;

            break;
          } else if (reward.createdAt < balance.timestamp) {
            // we have passed it
            break;
          }
        }
      }
    });

  Object.entries(stakingNfts).forEach(([key, position]) => {
    try {
      addPositionChartData(position);
    } catch (e) {
      console.error(position, key, e);
      if (position.symbol !== 'KEI') {
        delete stakingNfts[key];
      }
    }
  });

  return Object.values(stakingNfts);
}
